import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Send, Loader2, Sparkles, RotateCcw, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SimulationModule = ({ caseId, onClose }) => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/simulation/roles`);
      setRoles(Object.entries(response.data.roles).map(([key, value]) => ({
        id: key,
        ...(value as object)
      })));
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userInput.trim() || !selectedRole) return;

    const userMessage = { role: 'user', content: userInput };
    setConversation([...conversation, userMessage]);
    setUserInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/simulation/simulate`, {
        case_id: caseId,
        role: selectedRole,
        conversation_history: conversation,
        user_argument: userInput
      });

      const aiMessage = { role: 'assistant', content: response.data.response };
      setConversation(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Simulation error:', error);
      toast.error('Kunne ikke kjøre simulering');
    } finally {
      setLoading(false);
    }
  };

  const resetConversation = () => {
    setConversation([]);
    setUserInput('');
  };

  return (
    <div className="h-full flex flex-col">
      {!selectedRole ? (
        /* Role Selection */
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Juridisk Simulering</h2>
              <p className="text-gray-600">Velg en rolle for å teste dine argumenter</p>
            </div>
            {onClose && (
              <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            )}
          </div>

          <div className="grid gap-4">
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className="text-left p-5 rounded-xl border-2 border-gray-200 hover:border-purple-300 bg-white hover:shadow-lg transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                    {role.id === 'judge' ? '⚖️' :
                     role.id === 'opposing_lawyer' ? '🎯' :
                     role.id === 'regulator' ? '📋' :
                     role.id === 'mediator' ? '🤝' :
                     role.id === 'devils_advocate' ? '😈' : '💼'}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{role.name}</h3>
                    <p className="text-sm text-gray-600">{role.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Simulation Chat */
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-gray-900">
                    {roles.find(r => r.id === selectedRole)?.name}
                  </h2>
                  <span className="px-2 py-1 bg-purple-600 text-white rounded text-xs font-bold">
                    AI Simulering
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {roles.find(r => r.id === selectedRole)?.description}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={resetConversation}
                  variant="outline"
                  size="sm"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
                <Button
                  onClick={() => setSelectedRole(null)}
                  variant="outline"
                  size="sm"
                >
                  Bytt rolle
                </Button>
                {onClose && (
                  <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
            {conversation.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-10 h-10 text-purple-600" />
                </div>
                <p className="text-gray-600 mb-2">Start simulering</p>
                <p className="text-sm text-gray-500">
                  Presenter ditt argument, og AI vil respondere i rollen som {roles.find(r => r.id === selectedRole)?.name}
                </p>
              </div>
            ) : (
              conversation.map((msg, i) => (
                <div
                  key={i}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    msg.role === 'user' ? 'bg-gray-900 text-white' : 'bg-purple-600 text-white'
                  }`}>
                    {msg.role === 'user' ? '⚖️' : '🤖'}
                  </div>
                  
                  <div className={`max-w-[75%] ${msg.role === 'user' ? 'items-end' : ''}`}>
                    <div className={`rounded-2xl px-5 py-4 ${
                      msg.role === 'user'
                        ? 'bg-gray-900 text-white'
                        : 'bg-white border-2 border-purple-200 text-gray-900'
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
            
            {loading && (
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                </div>
                <div className="bg-white border-2 border-purple-200 rounded-2xl px-5 py-4">
                  <p className="text-sm text-gray-600">AI tenker...</p>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4 bg-white">
            <form onSubmit={sendMessage} className="flex gap-2">
              <textarea
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
                placeholder="Presenter ditt juridiske argument..."
                className="flex-1 resize-none border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={3}
              />
              <Button
                type="submit"
                disabled={loading || !userInput.trim()}
                className="bg-purple-600 hover:bg-purple-700 self-end"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>
            <p className="text-xs text-gray-500 mt-2">
              Shift+Enter for ny linje • Enter for å sende
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimulationModule;
